import { html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { type IconName, getIcon } from './helpers/icons'
import style from './style/zaptic-tag-style'

export enum Events {
    click = 'onClick',
    remove = 'onRemove',
}

export interface EventDetails {
    [Events.click]: undefined
    [Events.remove]: undefined
}

function createEvent<T extends Events, Detail extends EventDetails[T]>(type: T, detail: Detail) {
    return new CustomEvent<Detail>(type, detail && { detail })
}

@customElement('zaptic-tag')
export class Tag extends LitElement {
    static styles = style

    @property({ type: String })
    label?: string = ''

    @property({ type: String })
    labelTranslationKey?: string = ''

    @property({ type: String })
    icon?: IconName = undefined

    @property({ type: String })
    iconImage?: string = undefined

    @property({ type: Boolean })
    isShowingSelection: boolean = false

    @property({ type: Boolean })
    isActive: boolean = false

    @property({ type: Boolean })
    isDisabled: boolean = false

    @property({ type: Boolean })
    isRemovable: boolean = false

    @state()
    private isOverflowing: boolean = false

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        super.updated(changedProperties)

        if (changedProperties.has('label') === false) {
            return
        }

        this.checkOverflow()
    }

    private checkOverflow() {
        const wrapper = this.shadowRoot?.querySelector<HTMLElement>('.label-text')
        this.isOverflowing = wrapper ? wrapper.scrollWidth > wrapper.clientWidth : false
    }

    render() {
        return html`<div
            class="${classMap({
                'is-showing-selection': this.isShowingSelection,
                'is-active': this.isActive,
                'is-disabled': this.isDisabled,
                'is-removable': this.isRemovable,
                'has-primary-icon': !!this.icon,
                'has-image-icon': !!this.iconImage,
                tag: true,
            })}"
            part="tag"
            @click=${this.isRemovable ? nothing : () => this.dispatchEvent(createEvent(Events.click, undefined))}
            role=${this.isRemovable ? (nothing as any) : 'button'}
            data-tooltip=${this.isOverflowing ? this.label : nothing}
        >
            <div class="label">
                <slot name="iconImage"
                    >${this.iconImage ? html`<img src=${this.iconImage} class="icon-image" />` : nothing}</slot
                >

                <slot name="icon"
                    >${this.icon
                        ? html`<zaptic-icon icon-name=${this.icon} class="icon"></zaptic-icon>`
                        : nothing}</slot
                >
                <slot name="label">
                    <span class="label-text" part="label-text"
                        >${this.labelTranslationKey ? window.i18n(this.labelTranslationKey) : this.label}</span
                    >
                </slot>
                <slot name="iconSecondary">${this.isRemovable ? this.RemoveIcon() : nothing}</slot>
            </div>
        </div>`
    }

    RemoveIcon(label?: string) {
        return html`<div
            @click=${() => this.dispatchEvent(createEvent(Events.remove, undefined))}
            role="button"
            class="icon icon-remove"
            data-testid=${label ? `deselect-tag-${label}` : nothing}
        >
            ${getIcon('inverted-cross')}
        </div>`
    }
}
declare global {
    interface HTMLElementTagNameMap {
        'zaptic-tag': Tag
    }
}
